import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  EnergyPurchasesCard,
  ElectricVehicleCard,
} from './component/Dashboard/ScopeII'

import { ReportingCard } from "./component/Dashboard/ReportingCard";
import GuidelineModalToggleButton from "./component/Dashboard/GuidelineModalToggleButton";
import GuidelineModal from "./component/Dashboard/GuidelineModal";
import { DateRangePicker } from '../Table/component/Table'
import {
  ApexColumnChart,
  ApexLineChart,
  ApexPieChart,
} from './component/Charts'

import ColChart from './component/Charts/ColChart'

// ✅ Correct ReportingTable import path
import ReportingTable from '../Table/component/Table/ReportingTable'

import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'
import { useNavigate, useLocation } from 'react-router-dom'

const ScopeII = () => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [data, setData] = useState([]) // Energy Purchase data
  const [evData, setEvData] = useState([]) // Electric Vehicle data
  const [isLoading, setIsLoading] = useState(false)
  const [isEvLoading, setIsEvLoading] = useState(false)
  const [pieChartData, setPieChartData] = useState([0, 0]); // [stationary, mobile, fugitive]
  const [colChartData, setColChartData] = useState([[], []]); // 3 arrays of 12 months
  const year = new Date().getFullYear();

  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const location = useLocation()

  const handleToggleModal = () => setModalOpen(!isModalOpen)

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchChartData = async () => {
      try {
        // Fetch total emissions
        const totalRes = await axiosPrivate.get(`/dashboards/s2-total/${year}`, {
          signal: controller.signal
        });
        if (isMounted) setPieChartData(totalRes.data.data);

        // Fetch monthly emissions
        const monthlyRes = await axiosPrivate.get(`/dashboards/s2-monthly/${year}`, {
          signal: controller.signal
        });
        if (isMounted) setColChartData(monthlyRes.data.data);

      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Failed to fetch Scope 2 chart data:", err);
          if (err.response?.status === 403) {
            navigate("/sign-in", { state: { from: location }, replace: true });
          }
        }
      }
    };

    fetchChartData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [axiosPrivate, navigate, location, year]);


  // ✅ Fetch energy purchase data
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await axiosPrivate.get(
          '/energyPurchase/read-S2CA-records',
          { signal: controller.signal }
        )

        if (isMounted) {
          setData(response.data || [])
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch energy purchase data:', err)
          if (err.response?.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true })
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [axiosPrivate, navigate, location])

  // ✅ Fetch Electric Vehicle data
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchEvData = async () => {
      setIsEvLoading(true)
      try {
        const response = await axiosPrivate.get(
          '/electricVehicle/read-S2CB-records',
          { signal: controller.signal }
        )
        if (isMounted) {
          setEvData(response.data || [])
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch electric vehicle data:', err)
          if (err.response?.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true })
          }
        }
      } finally {
        setIsEvLoading(false)
      }
    }

    fetchEvData()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [axiosPrivate, navigate, location])

  // ✅ Define columns for the Energy Purchase table
  const columns = [
    { header: 'Emission Date', accessorKey: 'emissionDate' },
    { header: 'Report By', accessorKey: 'reportBy' },
    { header: 'Energy Type', accessorKey: 'energyType' },
    { header: 'Consumption (kWh)', accessorKey: 'consumption' },
  ]

  // ✅ Define columns for Electric Vehicle table
  const evColumns = [
    { header: 'Vehicle ID', accessorKey: 'vehicleId' },
    { header: 'Report By', accessorKey: 'reportBy' },
    { header: 'Energy Source', accessorKey: 'energySource' },
    { header: 'Distance Travelled (km)', accessorKey: 'distance' },
  ]

  // ✅ Limit to 5 rows, pad with empty rows if less
  const displayData = [
    ...data.slice(0, 5),
    ...Array.from({ length: Math.max(0, 5 - data.length) }, (_, i) => ({
      id: `empty-${i}`,
      emissionDate: '',
      reportBy: '',
      energyType: '',
      consumption: '',
    })),
  ].slice(0, 5)

  const displayEvData = [
    ...evData.slice(0, 5),
    ...Array.from({ length: Math.max(0, 5 - evData.length) }, (_, i) => ({
      id: `empty-ev-${i}`,
      vehicleId: '',
      reportBy: '',
      energySource: '',
      distance: '',
    })),
  ].slice(0, 5)

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto max-h-full max-w-full">
      <div className="justify-between flex">
        <h2 className="ml-4 mt-4">
          Scope 2: Energy Purchase & Electric Vehicle
        </h2>
        {/* <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
        /> */}
      </div>

      {/* This is the charts above */}
      <div className="grid pl-4 pr-4 rounded-3xl pt-4 text-maindarkbg dark:text-mainbg">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6 sm:rounded-lg">
            <ApexPieChart
              data={pieChartData}
              title={'Total Emission (Tonnes)'}
              height={400}
              labels={['Energy Purchase', 'Electric Vehicle']}
            />
          </div>
          <div className="col-span-12 lg:col-span-6 sm:rounded-lg">
            <ColChart
              data={colChartData}
              seriesNames={['Energy Purchase', 'Electric Vehicle']}
              title={"Category Breakdown"}
              height={400}
            />
          </div>
        </div>
      </div>

      {/* ✅ Energy Purchase Table */}
      <div className="px-4 mt-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Energy Purchases</h3>
          <Link to="/scope-ii/energy-purchases">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ReportingTable
            data={displayData}
            columns={columns}
            selectedRows={new Set()}
          // setSelectedRows={() => {}}
          // onRequestDelete={() => {}}
          // onEdit={() => {}}
          />
        )}
      </div>

      {/* ✅ Electric Vehicle Table */}
      <div className="px-4 mt-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Electric Vehicles</h3>
          <Link to="/scope-ii/electric-vehicle">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {isEvLoading ? (
          <div>Loading...</div>
        ) : (
          <ReportingTable
            data={displayEvData}
            columns={evColumns}
            selectedRows={new Set()}
          // setSelectedRows={() => {}}
          // onRequestDelete={() => {}}
          // onEdit={() => {}}
          />
        )}
      </div>

      {/* <div className="flex flex-col lg:flex-row justify-around items-center pt-10 px-1 lg:pl-15 xl:pl-20 mb-10">
        <EnergyPurchasesCard />
        <ElectricVehicleCard />
      </div> */}

      <GuidelineModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title="Scope II — Energy Indirect Emissions"
        subtitle="GHG Protocol Guidelines"
        content={
          <div className="space-y-3">
            <p>Scope II accounts for emissions from the generation of purchased or acquired electricity, steam, heating, and cooling consumed by the reporting company.</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Energy Purchases:</strong> Emissions from purchased electricity, steam, heat, or cooling used in your facilities.</li>
              <li><strong>Electric Vehicle:</strong> Emissions from electricity consumed by company electric vehicles not covered under Scope I.</li>
            </ul>
          </div>
        }
      />
    </div>


    //   <div className="flex flex-col lg:flex-row justify-around items-center pt-10 px-1 lg:pl-15 xl:pl-20 mb-10">
    //     <EnergyPurchasesCard />
    //     <ElectricVehicleCard />
    //   </div>
    //   <GuidelineModal
    //     isOpen={isModalOpen}
    //     onClose={handleToggleModal}
    //     title="Modal Title"
    //     subtitle="Modal Subtitle"
    //     content={<p>This is a guideline for scope II</p>}
    //   />
    // </div>
  )
}

export default ScopeII
